import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, Button, Card, CardActionArea, CardActions, CardContent, CardMedia, Stack, Tooltip, Typography, } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DisplayMode, ImageDisplay } from "../../../OrderFlow/OrderFlowProvider/OrderFlowTypes";
import { convertRgbOrHexToRgba } from "../BrandingProvider/branding-utils";
import { PriceChip } from "../PriceChip/PriceChip";
import { useContainerSettings } from "../../../Shared/hooks/UseContainerSettings/useContainerSettings";
import { PlusMinusControl } from "../ItemCard/components/PlusMinusControl";
import { ToggleCardDescription } from "./ToggleCardDescription";
/**
 * ToggleCard - Main purpose is for mutually exclusive selection.
 *              Card view with optional image and description display.
 * @param title
 * @param description
 * @param selected
 * @param itemDisplayMode
 * @param image
 * @param onClick
 * @param onMinusClick
 * @param onPlusClick
 * @param quantity
 * @param descriptionLines
 * @param showDescription
 * @param toggleMode
 * @param showImage
 * @param imageDisplayMode
 * @param imageHeight
 * @param pricingInfoChip
 * @returns
 */
export const ToggleCard = ({ title, description, selected, itemDisplayMode, image, onClick, onMinusClick, onPlusClick, quantity, descriptionLines, showDescription, showImage = true, toggleMode = true, imageDisplayMode = ImageDisplay.ImagePinnedToTop, imageHeight = undefined, pricingInfoChip = undefined, toolTipSettings = {
    buttonToolTipDesktop: true,
    buttonToolTipMobile: true,
    cardToolTipMobile: true,
}, }) => {
    const theme = useTheme();
    const { isMobile } = useContainerSettings();
    const shouldShowImage = imageDisplayMode !== ImageDisplay.NoImage && showImage;
    const shouldDisplayDescription = showDescription && description;
    const { buttonToolTipDesktop, buttonToolTipMobile, cardToolTipMobile } = toolTipSettings;
    const shouldShowCardToolTip = shouldDisplayDescription && isMobile && cardToolTipMobile;
    const shouldShowCardDescription = shouldDisplayDescription && (!isMobile || !cardToolTipMobile);
    const shouldShowButtonTooltip = isMobile ? buttonToolTipMobile : buttonToolTipDesktop;
    return itemDisplayMode == DisplayMode.Card ? (_jsx(Card, Object.assign({ sx: {
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            justifyContent: "flex-start",
            borderStyle: "solid",
            borderWidth: selected ? theme.spacing(0.25) : theme.spacing(0.125),
            borderColor: selected ? theme.palette.primary.main : "#B9BCBF",
            borderRadius: theme.spacing(1),
            position: "relative",
            gap: theme.spacing(1),
        }, elevation: 1, variant: "elevation", onClick: toggleMode ? onClick : undefined }, { children: _jsxs(CardActionArea, Object.assign({ sx: Object.assign({ width: "100%", flexGrow: 1, justifyContent: "flex-start", display: "flex", flexDirection: "column", alignItems: "center", padding: selected ? theme.spacing(1.75) : theme.spacing(1.875) }, (!toggleMode && { cursor: "default" })), disableRipple: toggleMode ? false : true }, { children: [shouldShowImage &&
                    (image ? (_jsx(CardMedia, { component: "img", sx: {
                            width: "auto",
                            maxWidth: "100%",
                            height: imageHeight,
                        }, image: image, title: title })) : (imageHeight && imageHeight > 0 && _jsx(Box, { sx: { height: imageHeight } }))), shouldShowImage && pricingInfoChip && (_jsx(Box, Object.assign({ sx: {
                        position: "absolute",
                        top: !selected ? theme.spacing(2) : `calc(${theme.spacing(2)} - 1px)`,
                        left: !selected ? theme.spacing(1) : `calc(${theme.spacing(1)} - 1px)`,
                        maxWidth: `calc(100% - ${theme.spacing(4)})`,
                    } }, { children: _jsx(PriceChip, { itemPrice: pricingInfoChip }) }))), _jsxs(CardContent, Object.assign({ sx: {
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: theme.spacing(1),
                        padding: 0,
                        "&:last-child": { pb: 0 },
                    } }, { children: [_jsxs(Stack, Object.assign({ direction: "row", justifyContent: "center", alignItems: "center", spacing: 1 }, { children: [_jsx(Typography, Object.assign({ variant: "h3", gutterBottom: true }, { children: title })), shouldShowCardToolTip && (_jsx(Tooltip, Object.assign({ title: description, arrow: true }, { children: _jsx(HelpOutlineIcon, { fontSize: "small", color: "primary" }) })))] })), shouldShowCardDescription && (_jsx(ToggleCardDescription, { description: description, descriptionLines: descriptionLines }))] })), !toggleMode && (_jsx(CardActions, Object.assign({ sx: {
                        position: "relative",
                        background: convertRgbOrHexToRgba(theme.palette.common.white, 0.8),
                        justifyContent: "center",
                        paddingTop: theme.spacing(1),
                    } }, { children: _jsx(PlusMinusControl, { handleAddClick: () => onPlusClick && onPlusClick(), handleMinusClick: () => onMinusClick && onMinusClick(), isAddDisabled: false, isMinusDisabled: (quantity !== null && quantity !== void 0 ? quantity : 0) === 0, quantity: quantity || 0 }) })))] })) }))) : (_jsx(Button, Object.assign({ color: "primary", variant: selected ? "contained" : "outlined", onClick: onClick, sx: { width: "100%", height: "100%" } }, { children: _jsxs(Stack, Object.assign({ direction: shouldShowButtonTooltip ? "row" : "column", justifyContent: "center", alignItems: "center", spacing: 1 }, { children: [_jsx(Typography, Object.assign({ variant: shouldShowButtonTooltip ? "body2" : "h4" }, { children: title })), shouldDisplayDescription && shouldShowButtonTooltip && (_jsx(Tooltip, Object.assign({ title: description, arrow: true }, { children: _jsx(HelpOutlineIcon, { fontSize: "small", sx: { color: "inherit" } }) }))), shouldDisplayDescription && !shouldShowButtonTooltip && (_jsx(ToggleCardDescription, { description: description, descriptionLines: descriptionLines }))] })) })));
};
